import { useEffect, useState, useCallback } from 'react';
import { getAuditLogs, exportAuditLogs, listCompanies, AuditLog, Company } from '../services/api';
import { fmtDateTimeSecJst } from '../utils/datetime';
import DemoAccessPanel from '../components/DemoAccessPanel';

const ACTION_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  // 運営管理者操作
  login:              { label: 'SA ログイン',        color: '#1d4ed8', bg: '#dbeafe' },
  company_create:     { label: '会社登録',            color: '#15803d', bg: '#dcfce7' },
  company_update:     { label: '会社情報更新',        color: '#0891b2', bg: '#cffafe' },
  company_delete:     { label: '会社削除',            color: '#dc2626', bg: '#fee2e2' },
  company_status:     { label: '会社ステータス変更',  color: '#9333ea', bg: '#f3e8ff' },
  company_meta:       { label: 'メモ更新',            color: '#64748b', bg: '#f1f5f9' },
  company_settings:   { label: 'アラート設定更新',    color: '#0891b2', bg: '#cffafe' },
  admin_create:       { label: '管理者追加',          color: '#15803d', bg: '#dcfce7' },
  admin_toggle:       { label: '管理者有効/無効',     color: '#c2410c', bg: '#fff7ed' },
  admin_password:     { label: '管理者PW変更',        color: '#c2410c', bg: '#fff7ed' },
  sa_create:          { label: 'SA追加',              color: '#7c3aed', bg: '#ede9fe' },
  sa_toggle:          { label: 'SA有効/無効',         color: '#7c3aed', bg: '#ede9fe' },
  sa_password:        { label: '自分のPW変更',        color: '#64748b', bg: '#f1f5f9' },
  sa_profile:         { label: 'プロフィール更新',    color: '#64748b', bg: '#f1f5f9' },
  broadcast:          { label: '一括メッセージ送信',  color: '#7c3aed', bg: '#ede9fe' },
  user_purge:         { label: 'ユーザーデータ削除',  color: '#dc2626', bg: '#fee2e2' },
  ip_registry_lookup: { label: 'IP登録情報の照会',    color: '#0f766e', bg: '#ccfbf1' },
  internal_network_add:    { label: '当社の回線を登録',   color: '#6d28d9', bg: '#ede9fe' },
  internal_network_update: { label: '当社の回線の名前変更', color: '#6d28d9', bg: '#ede9fe' },
  internal_network_delete: { label: '当社の回線の登録削除', color: '#b91c1c', bg: '#fee2e2' },
  // 管理者ログイン
  admin_login:        { label: 'ログイン',            color: '#0369a1', bg: '#e0f2fe' },
  admin_login_failed: { label: 'ログイン失敗',        color: '#dc2626', bg: '#fee2e2' },
  // テナント操作（打刻・ユーザー管理）
  timestamp_clock_in:  { label: '業務開始打刻',       color: '#15803d', bg: '#dcfce7' },
  timestamp_clock_out: { label: '業務終了打刻',       color: '#0891b2', bg: '#cffafe' },
  alcohol_check:       { label: 'アルコールチェック', color: '#d97706', bg: '#fef3c7' },
  user_create:         { label: 'ユーザー作成',       color: '#15803d', bg: '#dcfce7' },
  user_update:         { label: 'ユーザー更新',       color: '#0891b2', bg: '#cffafe' },
  user_delete:         { label: 'ユーザー削除',       color: '#dc2626', bg: '#fee2e2' },
  // 個人スマホアプリのアクセスログ（統合表示）
  personal_login:       { label: 'ログイン（個人）',   color: '#0369a1', bg: '#e0f2fe' },
  personal_face_login:  { label: '顔認証（個人）',     color: '#6d28d9', bg: '#ede9fe' },
  personal_correction:  { label: '修正依頼（個人）',   color: '#d97706', bg: '#fef3c7' },
};

const ROLE_LABEL: Record<string, string> = {
  owner: 'オーナー', admin: '管理者',
  shore_admin: '陸上管理者', system_admin: 'システム管理者',
  captain: '船長', crew: '船員',
};

const ROLE_COLOR: Record<string, { bg: string; color: string }> = {
  owner:        { bg: '#fef3c7', color: '#92400e' },
  admin:        { bg: '#e0f2fe', color: '#0369a1' },
  shore_admin:  { bg: '#dbeafe', color: '#1d4ed8' },
  system_admin: { bg: '#ede9fe', color: '#6d28d9' },
  captain:      { bg: '#fde68a', color: '#92400e' },
  crew:         { bg: '#f1f5f9', color: '#475569' },
};

const PAGE_SIZE = 50;

const ACTION_GROUPS = [
  { label: 'ログイン', options: [
    { value: 'login',              label: 'SAログイン' },
    { value: 'admin_login',        label: '管理者ログイン' },
    { value: 'admin_login_failed', label: 'ログイン失敗' },
  ]},
  { label: '打刻', options: [
    { value: 'timestamp_clock_in',  label: '業務開始打刻' },
    { value: 'timestamp_clock_out', label: '業務終了打刻' },
    { value: 'alcohol_check',       label: 'アルコールチェック' },
  ]},
  { label: 'ユーザー管理', options: [
    { value: 'user_create', label: 'ユーザー作成' },
    { value: 'user_update', label: 'ユーザー更新' },
    { value: 'user_delete', label: 'ユーザー削除' },
  ]},
  { label: '会社管理（SA）', options: [
    { value: 'company_create',   label: '会社登録' },
    { value: 'company_update',   label: '会社情報更新' },
    { value: 'company_delete',   label: '会社削除' },
    { value: 'company_status',   label: 'ステータス変更' },
    { value: 'admin_create',     label: '管理者追加' },
    { value: 'admin_toggle',     label: '管理者有効/無効' },
    { value: 'broadcast',        label: '一括メッセージ' },
  ]},
  { label: '個人アプリ', options: [
    { value: 'personal_login',       label: 'ログイン（個人）' },
    { value: 'personal_face_login',  label: '顔認証（個人）' },
    { value: 'personal_correction',  label: '修正依頼（個人）' },
  ]},
];

const selStyle: React.CSSProperties = {
  border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '7px 12px',
  fontSize: 13, outline: 'none', background: '#fff', minWidth: 140,
};

type LogTab = 'ops' | 'demo';
function readTab(): LogTab {
  try { return new URLSearchParams(window.location.search).get('tab') === 'demo' ? 'demo' : 'ops'; } catch { return 'ops'; }
}

export default function AuditLogPage() {
  // タブ: 操作ログ／体験デモのアクセス元（?tab=demo で直接開ける）
  const [tab, setTab] = useState<LogTab>(readTab);
  const switchTab = (t: LogTab) => {
    setTab(t);
    try { const u = new URL(window.location.href); if (t === 'demo') u.searchParams.set('tab', 'demo'); else u.searchParams.delete('tab'); window.history.replaceState(null, '', u.toString()); } catch { /* noop */ }
  };
  const [logs, setLogs]             = useState<AuditLog[]>([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterActor, setFilterActor]     = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]   = useState('');
  const [companies, setCompanies]   = useState<Company[]>([]);
  const [page, setPage]             = useState(0);
  const [exporting, setExporting]   = useState(false);

  useEffect(() => {
    listCompanies().then(setCompanies).catch(() => {});
  }, []);

  const load = useCallback((action: string, companyId: string, actor: string, dateFrom: string, dateTo: string, p: number) => {
    setLoading(true);
    getAuditLogs({
      action: action || undefined, company_id: companyId || undefined,
      actor: actor || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined,
      limit: PAGE_SIZE, offset: p * PAGE_SIZE,
    })
      .then(r => { setLogs(r.data); setTotal(r.total); })
      .catch(e => setError(e instanceof Error ? e.message : 'エラーが発生しました'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(filterAction, filterCompany, filterActor, filterDateFrom, filterDateTo, page); },
    [filterAction, filterCompany, filterActor, filterDateFrom, filterDateTo, page, load]);

  function handleActionChange(v: string)  { setFilterAction(v);  setPage(0); }
  function handleCompanyChange(v: string) { setFilterCompany(v); setPage(0); }
  function handleActorChange(v: string)    { setFilterActor(v);    setPage(0); }
  function handleDateFromChange(v: string) { setFilterDateFrom(v); setPage(0); }
  function handleDateToChange(v: string)   { setFilterDateTo(v);   setPage(0); }

  async function handleExport() {
    setExporting(true);
    setError('');
    try {
      await exportAuditLogs({
        action: filterAction || undefined, company_id: filterCompany || undefined,
        actor: filterActor || undefined, date_from: filterDateFrom || undefined, date_to: filterDateTo || undefined,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'エクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  }

  function formatDate(s: string) {
    return fmtDateTimeSecJst(s); // 日本時刻(Asia/Tokyo)で表示
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const tabBar = (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid #e2e8f0', marginBottom: 16 }}>
      {([['ops', '操作ログ'], ['demo', '📍 体験デモのアクセス元']] as Array<[LogTab, string]>).map(([k, l]) => (
        <button key={k} onClick={() => switchTab(k)}
          style={{ border: 'none', background: 'none', padding: '9px 14px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer',
            color: tab === k ? '#1d4ed8' : '#64748b', borderBottom: `2.5px solid ${tab === k ? '#2563eb' : 'transparent'}`, marginBottom: -1.5 }}>
          {l}
        </button>
      ))}
    </div>
  );

  if (tab === 'demo') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>操作ログ</h1>
        </div>
        {tabBar}
        <DemoAccessPanel />
      </div>
    );
  }

  return (
    <div>
      {tabBar}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>操作ログ</h1>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{total.toLocaleString()} 件</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>（個人アプリのログイン・修正依頼を含む）</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 会社フィルター */}
          <select value={filterCompany} onChange={e => handleCompanyChange(e.target.value)} style={selStyle}>
            <option value="">すべての会社</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {/* 操作フィルター */}
          <select value={filterAction} onChange={e => handleActionChange(e.target.value)} style={selStyle}>
            <option value="">すべての操作</option>
            {ACTION_GROUPS.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>
          {/* 実行者フィルター（部分一致） */}
          <input
            type="text" value={filterActor} placeholder="実行者で検索"
            onChange={e => handleActorChange(e.target.value)} style={selStyle}
          />
          {/* 日付範囲フィルター */}
          <input type="date" value={filterDateFrom} onChange={e => handleDateFromChange(e.target.value)} style={{ ...selStyle, minWidth: 0 }} title="開始日" />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>〜</span>
          <input type="date" value={filterDateTo} onChange={e => handleDateToChange(e.target.value)} style={{ ...selStyle, minWidth: 0 }} title="終了日" />
          <button onClick={() => load(filterAction, filterCompany, filterActor, filterDateFrom, filterDateTo, page)} style={refreshBtnStyle}>更新</button>
          <button onClick={handleExport} disabled={exporting} style={{ ...refreshBtnStyle, opacity: exporting ? 0.6 : 1, cursor: exporting ? 'default' : 'pointer' }}>
            {exporting ? 'エクスポート中...' : 'CSVエクスポート'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>{error}</div>}

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>読み込み中...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>ログがありません</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['日時', '会社', '実行者', '操作', '対象', '詳細', 'IP'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const ac = ACTION_LABELS[log.action] ?? { label: log.action, color: '#64748b', bg: '#f1f5f9' };
                const rc = ROLE_COLOR[log.actor_role] ?? { bg: '#f1f5f9', color: '#475569' };
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }} onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                    <td style={tdStyle}><span style={{ color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</span></td>
                    <td style={tdStyle}>
                      {log.company_name
                        ? <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>{log.company_name}</span>
                        : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.actor_name}</div>
                      <span style={{ background: rc.bg, color: rc.color, padding: '1px 5px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                        {ROLE_LABEL[log.actor_role] ?? log.actor_role}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ background: ac.bg, color: ac.color, padding: '3px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {ac.label}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {log.target_name && <div style={{ color: '#374151' }}>{log.target_name}</div>}
                      {log.company_name && log.target_type !== 'company' && !log.company_name && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{log.company_name}</div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: '#64748b', fontSize: 12, maxWidth: 200 }}>{log.detail || '—'}</td>
                    <td style={{ ...tdStyle, color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>{log.ip_address || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={pageBtnStyle}>← 前へ</button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{page + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={pageBtnStyle}>次へ →</button>
        </div>
      )}
    </div>
  );
}

const tdStyle: React.CSSProperties = { padding: '10px 14px', verticalAlign: 'top' };
const refreshBtnStyle: React.CSSProperties = { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const pageBtnStyle: React.CSSProperties = { background: '#fff', color: '#475569', border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
